import { createSlice } from '@reduxjs/toolkit'
import { PURGE } from 'redux-persist'

import { ThreadDetails } from 'features/referrals/types'
import type { RootState } from 'store/store'

import {
  ChatDetails,
  ChatMemberDetails,
  ChatNotificationDetails,
  DeleteChatDetails,
  DiscussionHeaderDataType,
  PinnedDiscussion,
} from '../types'

export interface DiscussionState {
  discussionDetail: string
  discussionList: string
  newChannelId: string
  selectedThread: ThreadDetails
  newChatDetails: ChatDetails | null
  deletedChatDetails: DeleteChatDetails | null
  chatNotificationList: ChatNotificationDetails[]
  unreadChatThreadCount: number
  newMemberDetails: ChatMemberDetails | null
  openNewDiscussionDialog: boolean
  headerData: DiscussionHeaderDataType
  /** The current user's pinned Discussion threads, newest pin first. */
  pinnedDiscussions: PinnedDiscussion[]
}
const initialState: DiscussionState = {
  discussionDetail: '',
  discussionList: '',
  newChannelId: '',
  selectedThread: {
    referralId: '',
    facilityName: '',
    patientName: '',
    threadId: '',
    patientId: 0,
    myMemberState: '',
  },
  newChatDetails: null,
  deletedChatDetails: null,
  newMemberDetails: null,
  chatNotificationList: [],
  unreadChatThreadCount: 0,
  openNewDiscussionDialog: false,
  headerData: {
    admissionDate: '',
    createdDate: '',
    dateOfBirth: '',
    id: 0,
    primaryPayor: '',
    referralId: '',
    referringHospital: '',
    status: '',
    statusId: 0,
    organizationId: 0,
    facilityId: 0,
  },
  pinnedDiscussions: [],
}
// slice
export const discussionSlice = createSlice({
  name: 'discussion',
  initialState,
  reducers: {
    setSelectedThreadId: (state, action) => {
      state.selectedThread = action.payload
    },
    setNewChannelId: (state, action) => {
      state.newChannelId = action.payload
    },

    setNewChatDetails: (state, action) => {
      state.newChatDetails = action.payload
    },
    setDeletedChatDetails: (state, action) => {
      state.deletedChatDetails = action.payload
    },
    setChatNotificationList: (state, action) => {
      state.chatNotificationList = action.payload
    },
    setUnreadChatThreadCount: (state, action) => {
      state.unreadChatThreadCount = action.payload
    },
    setNewMemberDetails: (state, action) => {
      state.newMemberDetails = action.payload
    },
    setOpenNewDiscussionDialog: (state, action) => {
      state.openNewDiscussionDialog = action.payload
    },
    setHeaderData: (state, action) => {
      state.headerData = action.payload
    },
    setPinnedDiscussions: (state, action) => {
      state.pinnedDiscussions = action.payload
    },
  },
  extraReducers: builder => {
    builder.addCase(PURGE, () => initialState)
  },
})
// Actions
export const discussionActions = {
  setSelectedThreadId: discussionSlice.actions.setSelectedThreadId,
  setNewChannelId: discussionSlice.actions.setNewChannelId,
  setNewChatDetails: discussionSlice.actions.setNewChatDetails,
  setDeletedChatDetails: discussionSlice.actions.setDeletedChatDetails,
  setChatNotificationList: discussionSlice.actions.setChatNotificationList,
  setUnreadChatThreadCount: discussionSlice.actions.setUnreadChatThreadCount,
  setNewMemberDetails: discussionSlice.actions.setNewMemberDetails,
  setOpenNewDiscussionDialog: discussionSlice.actions.setOpenNewDiscussionDialog,
  setHeaderData: discussionSlice.actions.setHeaderData,
  setPinnedDiscussions: discussionSlice.actions.setPinnedDiscussions,
}
// Selectors
export const getNewChannelId = (state: RootState) => state.discussion.newChannelId
export const getSelectedThread = (state: RootState) => state.discussion.selectedThread
export const getNewChatDetails = (state: RootState) => state?.discussion?.newChatDetails
export const getDeletedChatDetails = (state: RootState) => state?.discussion?.deletedChatDetails
export const getChatNotificationList = (state: RootState) => state?.discussion?.chatNotificationList
export const getUnreadChatThreadCount = (state: RootState) =>
  state?.discussion?.unreadChatThreadCount
export const getNewMemberDetails = (state: RootState) => state?.discussion?.newMemberDetails
export const getOpenNewDiscussionDialog = (state: RootState) =>
  state?.discussion?.openNewDiscussionDialog
export const getHeaderData = (state: RootState) => state?.discussion?.headerData
export const getPinnedDiscussionList = (state: RootState) => state?.discussion?.pinnedDiscussions

// Reducer
export default discussionSlice.reducer
